import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  Loader2,
  Save,
  RotateCcw,
  MapPin,
  Phone,
  Mail,
  Clock,
  Landmark,
  Navigation,
} from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "name", label: "Nama Rumah Sakit", icon: Landmark, placeholder: "RS Bhayangkara Tarakan" },
  { key: "affiliation", label: "Afiliasi", icon: Landmark, placeholder: "Polda Kalimantan Utara" },
  { key: "address_id", label: "Alamat (Bahasa Indonesia)", icon: MapPin, textarea: true, placeholder: "Jalan …, Kelurahan …, Kecamatan …, Kota …, 771xx" },
  { key: "address_en", label: "Alamat (English)", icon: MapPin, textarea: true, placeholder: "Street …, Sub-district …, City …, Province, Postal Code" },
  { key: "phone", label: "Telepon (tampilan)", icon: Phone, placeholder: "+62 822 4176 1118" },
  { key: "whatsapp", label: "WhatsApp (hanya angka, ex: 6282241761118)", icon: Phone, placeholder: "6282241761118" },
  { key: "email", label: "Email", icon: Mail, placeholder: "kontak@rsbhayangkaratarakan.id" },
  { key: "hours_id", label: "Jam Operasional (Indonesia)", icon: Clock, placeholder: "IGD 24 Jam | Poliklinik: ..." },
  { key: "hours_en", label: "Jam Operasional (English)", icon: Clock, placeholder: "ER 24 Hours | Clinic: ..." },
  { key: "latitude", label: "Latitude (koordinat GPS)", icon: Navigation, placeholder: "3.3987", type: "number", step: "0.0001" },
  { key: "longitude", label: "Longitude (koordinat GPS)", icon: Navigation, placeholder: "117.5924", type: "number", step: "0.0001" },
  { key: "maps_query", label: "Google Maps Query (fallback teks pencarian)", icon: Navigation, placeholder: "RS Bhayangkara Tarakan Jalan …" },
];

const HospitalInfoPanel = () => {
  const { authAxios } = useAuth();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const load = () => {
    setLoading(true);
    authAxios
      .get("/hospital-info")
      .then((r) => {
        const { updated_at, ...rest } = r.data || {};
        setForm(rest);
        setUpdatedAt(updated_at);
      })
      .catch(() => toast.error("Gagal memuat data"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Coerce numeric fields
      const payload = { ...form };
      ["latitude", "longitude"].forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) {
          delete payload[k];
        } else {
          const n = Number(payload[k]);
          if (Number.isFinite(n)) payload[k] = n;
        }
      });
      const r = await authAxios.put("/admin/hospital-info", payload);
      const { updated_at, ...rest } = r.data || {};
      setForm(rest);
      setUpdatedAt(updated_at);
      toast.success("Info rumah sakit tersimpan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset semua ke default? Data override akan dihapus.")) return;
    setSaving(true);
    try {
      // Empty payload clears overrides — server merges with defaults
      await authAxios.put("/admin/hospital-info", {});
      load();
      toast.success("Berhasil direset ke default");
    } catch (err) {
      toast.error("Gagal reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-hospital-info-panel" className="max-w-3xl">
      <div className="mb-6">
        <h2 className="font-heading text-2xl font-bold text-slate-900 mb-1">
          Info Rumah Sakit
        </h2>
        <p className="text-sm text-slate-500">
          Data ini muncul di halaman <b>Kontak</b>, <b>Footer</b>, <b>Schema.org SEO</b>, dan
          <b> Google Maps embed</b>. Kosongkan sebuah kolom untuk pakai nilai bawaan.
        </p>
        {updatedAt && (
          <div className="text-xs text-slate-400 mt-2">
            Terakhir diupdate: {new Date(updatedAt).toLocaleString("id-ID")}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-5">
        {FIELDS.map((f) => {
          const Icon = f.icon;
          const val = form[f.key] ?? "";
          return (
            <div key={f.key}>
              <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
                <Icon className="h-3.5 w-3.5" />
                {f.label}
              </label>
              {f.textarea ? (
                <textarea
                  data-testid={`admin-hospital-info-${f.key}`}
                  value={val}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
                />
              ) : (
                <input
                  data-testid={`admin-hospital-info-${f.key}`}
                  type={f.type || "text"}
                  step={f.step}
                  value={val}
                  onChange={(e) => setField(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
                />
              )}
            </div>
          );
        })}

        <div className="pt-4 border-t border-slate-100 flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-hospital-info-save"
            className="inline-flex items-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Simpan Perubahan
          </button>
          <button
            onClick={handleReset}
            disabled={saving}
            data-testid="admin-hospital-info-reset"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-red-700 px-3 py-2.5 rounded-xl transition"
          >
            <RotateCcw className="h-4 w-4" />
            Reset ke Default
          </button>
        </div>
      </div>

      <div className="mt-4 text-xs text-slate-400 leading-relaxed">
        Tips: untuk mendapatkan koordinat GPS presisi, buka Google Maps → klik kanan di lokasi
        RS → koordinat akan tampil, klik untuk copy. Format: <code>latitude, longitude</code>.
      </div>
    </div>
  );
};

export default HospitalInfoPanel;
